import React, { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Settings2, Check, Bot, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import type { FilteredConfigField } from '@/types/tag-config';
import FirebaseService, { type Tag } from '@/lib/firebase';
import { getAuth } from 'firebase/auth';
import type { AssistantDisplay } from '@/types';

// Type definitions for tool configuration
export interface ToolConfig {
  id: string;
  config: Record<string, any>;
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'radio' | 'assistant_multiselect';
  placeholder?: string;
  options?: { label: string; value: string }[];
  required?: boolean;
  defaultValue?: any;
  min?: number;  // For number type
  max?: number;  // For number type
}

interface ToolConfiguratorProps {
  toolId: string;
  currentConfig?: Record<string, any>;
  onConfigUpdate: (toolId: string, config: Record<string, any>) => void;
  configFields: FilteredConfigField[];
  onToolEnable?: (toolId: string) => void; // Add callback to auto-enable tool
  availableTags?: Tag[]; // Available tags for display
  availableAssistants?: AssistantDisplay[]; // Optional assistants list for assistant selector
}

const ToolConfigurator = ({
  toolId,
  currentConfig = {},
  onConfigUpdate,
  configFields,
  onToolEnable,
  availableTags = [],
  availableAssistants = []
}: ToolConfiguratorProps) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [localConfig, setLocalConfig] = React.useState(currentConfig);
  const [mcpDiscovering, setMcpDiscovering] = React.useState(false);
  const [mcpToolOptions, setMcpToolOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [mcpError, setMcpError] = React.useState<string | null>(null);
  const [mcpSuccess, setMcpSuccess] = React.useState<string | null>(null);
  const [lastDiscoveredUrl, setLastDiscoveredUrl] = React.useState<string | null>(null);
  
  // Initialize mcpToolOptions from existing config if available
  React.useEffect(() => {
    if (toolId === 'external_mcp' && currentConfig?.discovered_tools) {
      setMcpToolOptions(currentConfig.discovered_tools);
      setLastDiscoveredUrl(currentConfig.url);
    }
  }, [toolId, currentConfig]);

  // MCP Discovery Hook - Auto-discover tools when URL changes
  useEffect(() => {
    if (toolId === 'external_mcp' && localConfig?.url && localConfig.url !== lastDiscoveredUrl) {
      // Clear previous states
      setMcpError(null);
      setMcpSuccess(null);
      setMcpToolOptions([]);

      const timer = setTimeout(async () => {
        setMcpDiscovering(true);
        try {
          const response = await fetch('/api/proxy', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              endpoint: '/direct/tools/mcp/discover',
              url: localConfig.url,
              auth: localConfig.auth || undefined
            })
          });

          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.detail || `Server error: ${response.status}`);
          }
          
          if (data.success && data.tools && data.tools.length > 0) {
            // Transform discovered tools into select options
            const toolOptions = data.tools.map((tool: any) => ({
              value: tool.name,
              label: `${tool.name} - ${tool.description?.substring(0, 50) || 'No description'}...`
            }));

            setMcpToolOptions(toolOptions);

            // Auto-select all tools by default
            const allToolNames = data.tools.map((tool: any) => tool.name);
            const updatedConfig = {
              ...localConfig,
              selected_tools: allToolNames,
              discovered_tools: toolOptions  // Store discovered tools in config
            };
            setLocalConfig(updatedConfig);
            onConfigUpdate(toolId, updatedConfig);
            
            // Mark this URL as discovered to prevent re-triggering
            setLastDiscoveredUrl(localConfig.url);

            setMcpSuccess(`✅ Successfully discovered ${data.tools.length} tools from ${localConfig.url}`);
            console.log(`Discovered ${data.tools.length} MCP tools from ${localConfig.url}`);
          } else if (data.success && data.tools && data.tools.length === 0) {
            setMcpError('No tools found on this MCP server. The server may not expose any tools or may not be compatible.');
            setMcpToolOptions([]);
          } else {
            setMcpError(data.error || 'Failed to discover tools from MCP server');
            setMcpToolOptions([]);
          }
        } catch (error: any) {
          console.error('Failed to discover MCP tools:', error);
          let errorMessage = 'Failed to connect to MCP server. ';
          
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            errorMessage += 'Network error - check if the URL is correct and the server is accessible.';
          } else if (error.message.includes('400')) {
            errorMessage += 'Bad request - check if the URL format is correct.';
          } else if (error.message.includes('404')) {
            errorMessage += 'Server not found - the MCP endpoint may not exist at this URL.';
          } else if (error.message.includes('500')) {
            errorMessage += 'Server error - the MCP server encountered an internal error.';
          } else {
            errorMessage += error.message || 'Unknown error occurred.';
          }
          
          setMcpError(errorMessage);
          setMcpToolOptions([]);
        }
        setMcpDiscovering(false);
      }, 1000); // 1 second debounce

      return () => clearTimeout(timer);
    } else if (toolId === 'external_mcp' && !localConfig?.url) {
      // Clear states when URL is empty
      setMcpError(null);
      setMcpSuccess(null);
      setMcpToolOptions([]);
      setLastDiscoveredUrl(null);
    }
  }, [toolId, localConfig?.url, localConfig?.auth, lastDiscoveredUrl, onConfigUpdate]);

  // Helper function to get tag color (matching ToolSelector styling)
  const getTagColor = (tagId: string) => {
    const tag = availableTags.find(t => t.id === tagId);
    const tagName = tag?.name || tagId;
    
    if (tagName === 'admin-tools') return 'bg-red-100 text-red-800 border-red-200';
    if (tagName === 'beta-testers') return 'bg-purple-100 text-purple-800 border-purple-200';
    if (tagName === 'finance-team') return 'bg-green-100 text-green-800 border-green-200';
    if (tagName === 'ONE') return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleChange = (key: string, value: any) => {
    setLocalConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const areRequiredFieldsFilled = (): boolean => {
    return configFields
      .filter(field => field.required)
      .every(field => {
        const value = localConfig[field.key];
        if (field.type === 'checkbox') return true; // Checkboxes are always valid
        if (field.key === 'customBucketUrl') {
          // Custom bucket URL is only required if bucketUrl is 'custom'
          return localConfig.bucketUrl !== 'custom' || (value && value.trim() !== '');
        }
        if (field.key === 'customConfigId') {
          // Custom config ID is only required if configId is 'custom'
          return localConfig.configId !== 'custom' || (value && value.trim() !== '');
        }
        if (field.key === 'customDatastoreId') {
          // Custom datastore ID is only required if datastore_id is 'custom'
          return localConfig.datastore_id !== 'custom' || (value && value.trim() !== '');
        }
        if (field.key === 'bucketUrl' && toolId === 'document_search_agent') {
          // Bucket URL is only required if preset_config is 'custom'
          return localConfig.preset_config !== 'custom' || (value && value.trim() !== '');
        }
        if (field.key === 'datastore_id' && toolId === 'document_search_agent') {
          // Datastore ID is only required if preset_config is 'custom'
          return localConfig.preset_config !== 'custom' || (value && value.trim() !== '');
        }
        // For assistant multiselect, check if array has at least one item
        if (field.type === 'assistant_multiselect') {
          return Array.isArray(value) && value.length > 0;
        }
        return value && value.trim() !== '';
      });
  };

  const handleSave = () => {
    // Process the config before saving
    let processedConfig = { ...localConfig };
    
    // Handle file-browser bucket URL logic
    if (toolId === 'file-browser') {
      if (processedConfig.bucketUrl === 'custom') {
        processedConfig.bucketUrl = processedConfig.customBucketUrl || '';
      }
      // Remove the custom field since it's not needed in the final config
      delete processedConfig.customBucketUrl;
    }
    
    // Handle vertex_search_app config ID logic
    if (toolId === 'vertex_search_app') {
      if (processedConfig.configId === 'custom') {
        processedConfig.configId = processedConfig.customConfigId || '';
      }
      // Remove the custom field since it's not needed in the final config
      delete processedConfig.customConfigId;
    }
    
    // Handle vertex_search datastore ID logic
    if (toolId === 'vertex_search' || toolId === 'vertex_search_first') {
      if (processedConfig.datastore_id === 'custom') {
        processedConfig.datastore_id = processedConfig.customDatastoreId || '';
      }
      // Remove the custom field since it's not needed in the final config
      delete processedConfig.customDatastoreId;
    }
    
    // Handle document_search_agent preset config logic
    if (toolId === 'document_search_agent') {
      if (processedConfig.preset_config === 'energy_docs') {
        processedConfig.bucketUrl = 'aitana-documents-bucket';
        processedConfig.datastore_id = 'aitana3';
      } else if (processedConfig.preset_config === 'public_demo') {
        processedConfig.bucketUrl = 'aitana-public-documents';
        processedConfig.datastore_id = 'aitana_public_welcome';
      } else if (processedConfig.preset_config === 'finance_analysis') {
        processedConfig.bucketUrl = 'finance-bucket';
        processedConfig.datastore_id = 'finance_datastore';
      }
      // For custom, the individual bucketUrl and datastore_id values are already set
      // Remove the preset_config field since it's not needed in the final config
      delete processedConfig.preset_config;
    }
    
    // Handle aitana-agent assistant selection
    if (toolId === 'aitana-agent' && processedConfig.selected_assistants) {
      // Convert array of assistant IDs to comma-separated string for assistant_ids field
      processedConfig.assistant_ids = processedConfig.selected_assistants.join(', ');
      
      // Build emissary_configs array from selected assistants
      const emissaryConfigs = processedConfig.selected_assistants.map((assistantId: string) => {
        const assistant = availableAssistants.find((a: AssistantDisplay) => a.assistantId === assistantId);
        if (!assistant) return null;
        
        return {
          assistant_id: assistantId,
          name: assistant.name,
          template_id: assistant.templateId,
          description: assistant.templateId || 'Custom Assistant',
          avatar: assistant.avatar,
          owner_email: assistant.ownerEmail,
          // Include initial message and instructions for context
          initial_message: assistant.initialMessage || '',
          initial_instructions: assistant.initialInstructions || '',
          // Include tool configuration so backend knows what this assistant can do
          tools: assistant.tools || [],
          tool_configs: assistant.toolConfigs || {},
          // Include documents this assistant has access to
          initial_documents: assistant.initialDocuments || [],
          // Include access control information
          access_control: assistant.accessControl || { type: 'private' },
          // Include usage statistics for decision making
          usage_count: assistant.usageCount || 0,
          created_at: assistant.createdAt,
          last_accessed_at: assistant.lastAccessedAt
        };
      }).filter(Boolean); // Remove any null entries
      
      processedConfig.emissary_configs = emissaryConfigs;
      
      // Remove the selected_assistants field since it's not needed in the final config
      delete processedConfig.selected_assistants;
    }
    
    onConfigUpdate(toolId, processedConfig);
    setIsEditing(false);
    
    // Auto-enable the tool after successful configuration
    if (onToolEnable) {
      onToolEnable(toolId);
    }
  };

  if (!isEditing) {
    const isConfigured = Object.keys(currentConfig).length > 0 && areRequiredFieldsFilled();
    
    return (
      <div className="flex items-center justify-between p-2 mt-2 bg-muted rounded-lg">
        <div className="text-xs text-muted-foreground">
          {isConfigured ? 'Configured' : 'Needs configuration'}
        </div>
        <Button 
          variant={isConfigured ? "ghost" : "default"}
          size="sm"
          onClick={() => setIsEditing(true)}
          className={!isConfigured ? "gap-2" : ""}
        >
          <Settings2 className="h-4 w-4" />
          {!isConfigured && <span>Configure</span>}
        </Button>
      </div>
    );
  }

  return (
    <Card className="mt-2 overflow-visible">
      <CardContent className="p-3 space-y-3 overflow-visible">
        {configFields
          .filter((field) => {
            // Filter out custom fields based on parent selection
            if (field.key === 'customBucketUrl' && localConfig.bucketUrl !== 'custom') {
              return false;
            }
            if (field.key === 'customConfigId' && localConfig.configId !== 'custom') {
              return false;
            }
            if (field.key === 'customDatastoreId' && localConfig.datastore_id !== 'custom') {
              return false;
            }
            // For document_search_agent, only show bucket and datastore_id fields when preset_config is 'custom'
            if (toolId === 'document_search_agent' && field.key === 'bucket' && localConfig.preset_config !== 'custom') {
              return false;
            }
            if (toolId === 'document_search_agent' && field.key === 'datastore_id' && localConfig.preset_config !== 'custom') {
              return false;
            }
            // Only show Anthropic web search option when an Anthropic model is selected
            if (field.key === 'enable_anthropic_web_search' && 
                !localConfig.smart_tool?.startsWith('anthropic')) {
              return false;
            }
            return true;
          })
          .map((field) => (
            <div key={field.key} className="space-y-1">
              <Label className="text-xs">{field.label}</Label>
              
              {field.type === 'text' || field.type === 'number' ? (
                <Input
                  type={field.type}
                  value={localConfig[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="h-8"
                  {...(field.type === 'number' && field.min !== undefined ? { min: field.min } : {})}
                  {...(field.type === 'number' && field.max !== undefined ? { max: field.max } : {})}
                />
              ) : field.type === 'select' ? (
                // Special handling for MCP selected_tools field
                field.key === 'selected_tools' && toolId === 'external_mcp' ? (
                  <div className="space-y-2">
                    {mcpDiscovering ? (
                      <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        Discovering tools from MCP server...
                      </div>
                    ) : mcpError ? (
                      <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                        ❌ {mcpError}
                      </div>
                    ) : mcpSuccess ? (
                      <div className="text-sm text-green-600 bg-green-50 p-2 rounded border border-green-200">
                        {mcpSuccess}
                      </div>
                    ) : null}
                    
                    {mcpToolOptions.length > 0 ? (
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground mb-2">
                          Select tools to use:
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {mcpToolOptions.map((option) => (
                            <div key={option.value} className="flex items-center space-x-2">
                              <Checkbox
                                id={`tool-${option.value}`}
                                checked={(localConfig[field.key] || []).includes(option.value)}
                                onCheckedChange={(checked) => {
                                  const currentTools = localConfig[field.key] || [];
                                  const updatedTools = checked
                                    ? [...currentTools, option.value]
                                    : currentTools.filter((tool: string) => tool !== option.value);
                                  handleChange(field.key, updatedTools);
                                }}
                              />
                              <label
                                htmlFor={`tool-${option.value}`}
                                className="text-xs cursor-pointer flex-1"
                                title={option.label}
                              >
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(localConfig[field.key] || []).length} of {mcpToolOptions.length} tools selected
                        </div>
                      </div>
                    ) : !mcpError && !mcpDiscovering ? (
                      <div className="text-xs text-muted-foreground bg-gray-50 p-2 rounded">
                        {localConfig?.url ? '⏳ Enter a valid MCP server URL above to discover tools' : field.placeholder || 'Enter URL above to discover tools'}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  // Standard select field
                  <Select
                    value={localConfig[field.key] || ''}
                    onValueChange={(value) => handleChange(field.key, value)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="z-50" position="popper" sideOffset={5}>
                      {field.options && field.options.length > 0 ? (
                        field.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center justify-between w-full">
                              <span>{option.label}</span>
                              {option.tags && option.tags.length > 0 && (
                                <div className="flex gap-1 ml-2">
                                  {option.tags.map(tagId => {
                                    const tag = availableTags.find(t => t.id === tagId);
                                    const tagName = tag?.name || tagId;
                                    
                                    return (
                                      <Badge 
                                        key={tagId}
                                        variant="outline" 
                                        className={`text-xs py-0 px-1 h-4 ${getTagColor(tagId)}`}
                                      >
                                        {tagName}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )
              ) : field.type === 'radio' ? (
                <div className="space-y-2">
                  {field.options && field.options.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`${field.key}-${option.value}`}
                        name={field.key}
                        value={option.value}
                        checked={(localConfig[field.key] || field.defaultValue) === option.value}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="h-4 w-4"
                      />
                      <label
                        htmlFor={`${field.key}-${option.value}`}
                        className="text-sm font-normal leading-none cursor-pointer flex items-center gap-2"
                      >
                        <span>{option.label}</span>
                        {option.tags && option.tags.length > 0 && (
                          <div className="flex gap-1">
                            {option.tags.map(tagId => {
                              const tag = availableTags.find(t => t.id === tagId);
                              const tagName = tag?.name || tagId;
                              
                              return (
                                <Badge 
                                  key={tagId}
                                  variant="outline" 
                                  className={`text-xs py-0 px-1 h-4 ${getTagColor(tagId)}`}
                                >
                                  {tagName}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`${field.key}-checkbox`}
                    checked={!!localConfig[field.key]}
                    onCheckedChange={(checked) => 
                      handleChange(field.key, checked === true)
                    }
                  />
                  <label
                    htmlFor={`${field.key}-checkbox`}
                    className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {field.placeholder || "Enable"}
                  </label>
                </div>
              ) : field.type === 'assistant_multiselect' ? (
                <div className="space-y-3">
                  {/* Available Assistants Section */}
                  <div>
                    <div className="text-xs font-medium mb-2">Available Assistants</div>
                    {availableAssistants.length === 0 ? (
                      <div className="text-xs text-muted-foreground">No assistants available</div>
                    ) : (
                      <ScrollArea className="h-24 border rounded-md p-2">
                        <div className="space-y-1">
                          {availableAssistants.map((assistant: AssistantDisplay) => {
                            const selectedIds = localConfig[field.key] || [];
                            const isSelected = selectedIds.includes(assistant.assistantId);
                            
                            return (
                              <div
                                key={assistant.assistantId}
                                className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer hover:bg-muted/50 ${
                                  isSelected ? 'bg-muted/30 border-primary/30' : ''
                                }`}
                                onClick={() => {
                                  const currentIds = localConfig[field.key] || [];
                                  const newIds = isSelected
                                    ? currentIds.filter((id: string) => id !== assistant.assistantId)
                                    : [...currentIds, assistant.assistantId];
                                  handleChange(field.key, newIds);
                                }}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Avatar className="h-5 w-5">
                                    <AvatarImage src={assistant.avatar} alt={assistant.name} />
                                    <AvatarFallback className="text-xs">{assistant.name?.[0] ?? '?'}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate">{assistant.name}</div>
                                  </div>
                                </div>
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                                  isSelected ? 'bg-primary border-primary' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    )}
                  </div>

                  {/* Selected Assistants with Ordering */}
                  {localConfig[field.key] && localConfig[field.key].length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium">
                          Call Order {localConfig.call_strategy === 'sequential' ? '(Sequential)' : '(Parallel)'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {localConfig[field.key].length} assistant{localConfig[field.key].length !== 1 ? 's' : ''} selected
                        </div>
                      </div>
                      <div className="space-y-2 border rounded-md p-2 bg-muted/10">
                        {localConfig[field.key].map((assistantId: string, index: number) => {
                          const assistant = availableAssistants.find((a: AssistantDisplay) => a.assistantId === assistantId);
                          if (!assistant) return null;
                          
                          const moveUp = () => {
                            if (index === 0) return;
                            const currentIds = [...localConfig[field.key]];
                            [currentIds[index - 1], currentIds[index]] = [currentIds[index], currentIds[index - 1]];
                            handleChange(field.key, currentIds);
                          };
                          
                          const moveDown = () => {
                            if (index === localConfig[field.key].length - 1) return;
                            const currentIds = [...localConfig[field.key]];
                            [currentIds[index], currentIds[index + 1]] = [currentIds[index + 1], currentIds[index]];
                            handleChange(field.key, currentIds);
                          };
                          
                          const remove = () => {
                            const currentIds = localConfig[field.key] || [];
                            const newIds = currentIds.filter((id: string) => id !== assistant.assistantId);
                            handleChange(field.key, newIds);
                          };
                          
                          return (
                            <div key={assistantId} className="flex items-center gap-2 p-2 border rounded-md bg-white">
                              {/* Order number */}
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                                {index + 1}
                              </div>
                              
                              {/* Assistant info */}
                              <div className="flex items-center gap-2 flex-1">
                                <Avatar className="h-5 w-5">
                                  <AvatarImage src={assistant.avatar} alt={assistant.name} />
                                  <AvatarFallback>{assistant.name?.[0] ?? '?'}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{assistant.name}</div>
                                </div>
                              </div>
                              
                              {/* Order controls */}
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={moveUp}
                                  disabled={index === 0}
                                  title="Move up"
                                >
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={moveDown}
                                  disabled={index === localConfig[field.key].length - 1}
                                  title="Move down"
                                >
                                  <ArrowDown className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                  onClick={remove}
                                  title="Remove"
                                >
                                  ×
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-xs text-muted-foreground italic">
                        {localConfig.call_strategy === 'sequential' ? (
                          <>💡 Sequential: Assistants will be called in the order shown above. Each assistant receives conversation history from previous assistants.</>
                        ) : (
                          <>💡 Parallel: All assistants will be called simultaneously with the same initial context. Order shown is for display purposes only.</>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!areRequiredFieldsFilled()}
          >
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ToolConfigurator;